'use strict';

/**
 * @readonly
 * @enum {string}
 */
var SFCCCardTypes = {
    /** American Express */
    AmericanExpress: 'Amex',
    /** Bancontact */
    Bancontact: 'Bancontact',
    /** Carte Bancaire */
    CarteBancaire: 'Carte Bancaire',
    /** Diners Club International */
    Diners: 'Diners',
    /** Diners Club International */
    DinersClub: 'DinersClub',
    /** Discover */
    Discover: 'Discover',
    /** JCB */
    JCB: 'JCB',
    /** Maestro. Debit card of Mastercard */
    Maestro: 'Maestro',
    /** Master Card */
    Master: 'Master',
    /** MasterCard */
    Mastercard: 'Mastercard',
    /** Union Pay */
    UnionPay: 'CUP',
    /** Visa */
    Visa: 'Visa'
};

/** @typedef {('Amex' | 'Bancontact' | 'Carte Bancaire' | 'Diners' | 'DinersClub' | 'Discover' | 'JCB' | 'Maestro' | 'Master' | 'Mastercard' | 'CUP' | 'Visa')} SFCCCardType */

/**
 * SFRA Address model
 * JSDoc with some common properties.
 * More properties: /models/address.js
 * @typedef {object} Address
 * @property {object|null} address
 * @property {string} address.address1
 * @property {string} address.address2
 * @property {string} address.addressId
 * @property {string} address.city
 * @property {string} address.companyName
 * @property {object} [address.countryCode]
 * @property {string} address.countryCode.displayValue
 * @property {string} address.countryCode.value
 * @property {object} [address.custom]
 * @property {string} [address.custom.byl_cif]
 * @property {string} [address.custom.byl_cifType]
 * @property {string} [address.custom.byl_cityLabel]
 * @property {string} [address.custom.byl_colonia]
 * @property {string} [address.custom.byl_comuna]
 * @property {string} [address.custom.byl_region]
 * @property {string} address.firstName
 * @property {string} address.ID
 * @property {string} address.jobTitle
 * @property {string} address.lastName
 * @property {string} address.phone
 * @property {string} address.phoneBody
 * @property {string} address.phonePrefix
 * @property {string} address.postalCode
 * @property {string} address.postBox
 * @property {string} address.salutation
 * @property {string} address.secondName
 * @property {string} address.stateCode
 * @property {string} address.suffix
 * @property {string} address.suite
 * @property {string} address.title
 */

/**
 * SFRA ShippingMethod model
 * JSDoc with some common properties.
 * More properties: /models/shipping/shippingMethod.js
 * @typedef {object} ShippingMethod
 * @property {boolean} default
 * @property {string} description
 * @property {string} displayName
 * @property {boolean} dropPointEnabled
 * @property {string} estimatedArrivalTime
 * @property {boolean} isExpress
 * @property {string} ID
 * @property {boolean} isHiddenInAppAndroid
 * @property {boolean} isHiddenInAppIos
 * @property {boolean} isHiddenInAssistedShopping
 * @property {boolean} isHiddenInWeb
 * @property {boolean} selected
 * @property {string} shippingCost
 * @property {number} shippingCostValue
 * @property {boolean} storePickupEnabled
 */

/**
 * SFRA Shipping model
 * JSDoc with some common properties.
 * More properties: /models/shipping.js
 * @typedef {object} Shipping
 * @property {ShippingMethod[]} applicableShippingMethods - Aplicable shipping methods array
 * @property {string} giftMessage - The value set for gift message or null if no value set.
 * @property {boolean} isGift - Returns true if this line item represents a gift, false otherwise.
 * @property {string|boolean} matchingAddressId - Shipping matching address ID or false
 * @property {string} pickupInstoreUrl - In store pickup shipping url
 * @property {ProductLineItem[]} productLineItems - Shipping Product Line Items
 * @property {ShippingMethod} selectedShippingMethod - Shipping Method
 * @property {Address} shippingAddress - Shipping Address
 * @property {string} UUID - Shipping Unique Identifier
 */

/**
 * SFRA Payment model
 * JSDoc with some common properties.
 * More properties: /models/payment.js
 * @typedef {object} Payment
 * @property {object[]} applicablePaymentCards Array of objects that contain information about applicable payment cards for current basket.
 * @property {object[]} applicablePaymentMethods Information about the applicable payment methods
 * @property {string} applicablePaymentMethods.ID
 * @property {string} applicablePaymentMethods.name
 * @property {PaymentInstrument[]} selectedPaymentInstruments Array of objects that contain information about the selected payment instruments
 */

/**
 * @typedef {object} PaymentInstrument
 * @property {string} name The name of the payment instrument
 * @property {string} paymentMethod The identifier of the payment method represented by this payment instrument.
 * @property {number} amount The value of the amount of the payment transaction.
 *
 * @property {SFCCCardTypes|string} [selectedAdyenPM] The payment method of the payment instrument of int_adyen_SFRA
 * @property {string} [selectedIssuerName] The issuer name of the payment instrument of int_adyen_SFRA
 * @property {*} [adyenAdditionalPaymentData] Additional payment data for Adyen
 * @property {string} [adyenAction] Action data from payment response of Adyen
 * @property {string} [installments] Card installments for Adyen
 *
 * @property {string} [expirationMonth] The expiration month of the credit card
 * @property {string} [expirationYear] The expiration year of the credit card
 * @property {string} [lastFour] Last four digits of the credit card number
 * @property {string} [maskedCreditCardNumber] The masked number of the credit card
 * @property {string} [owner] The holder of the credit card
 * @property {string} [type] The type of the credit card
 *
 * @property {string} [giftCertificateCode] The code of the gift certificate
 * @property {string} [maskedGiftCertificateCode] The masked code of the gift certificate
 *
 * @property {string} [payByLink_email] The email for AdyenPayByLink or MercadopagoPayByLink
 * @property {string} [payByLink_phone] The phone for AdyenPayByLink or MercadopagoPayByLink
 *
 * @property {string} [adyenPOS_email] The email for AdyenPOS
 * @property {string} [adyenPOS_phone] The phone for AdyenPOS
 * @property {string} [adyenPOS_serviceId] The ID of the service for AdyenPOS
 *
 * @property {string} [paymentCategory] The ID of the payment category for Klarna
 * @property {string} [categoryName] The name of the payment category for Klarna
 */

/**
 * SFRA Billing model
 * JSDoc with some common properties.
 * More properties: /models/billing.js
 * @typedef {object} Billing
 * @property {Address} billingAddress - Billing Address
 * @property {string} matchingAddressId - Billing address matching address ID
 * @property {Payment} payment - Payment data
 */

/**
 * SFRA Totals model
 * JSDoc with some common properties.
 * More properties: /models/totals.js
 * @typedef {object} Totals
 * @property {object[]} [discounts]
 * @property {boolean} discounts.applied
 * @property {string} discounts.couponCode
 * @property {object[]} discounts.relationship
 * @property {string} discounts.relationship.callOutMsg
 * @property {string} discounts.type
 * @property {string} discounts.UUID
 * @property {boolean} discounts.valid
 * @property {string} [discountsHtml]
 * @property {string} floatTotal
 * @property {string} grandTotal
 * @property {boolean} [isFree]
 * @property {string} netGrandTotal
 * @property {object} orderLevelDiscountTotal
 * @property {string} orderLevelDiscountTotal.formatted
 * @property {string} orderLevelDiscountTotal.value
 * @property {null} [priceAdjustments]
 * @property {object} shippingLevelDiscountTotal
 * @property {string} shippingLevelDiscountTotal.formatted
 * @property {string} shippingLevelDiscountTotal.value
 * @property {string} subTotal
 * @property {string} totalShippingCost
 * @property {string} totalTax
 */

/**
 * SFRA Cart model
 * JSDoc with some common properties.
 * More properties: /models/cart.js
 * @typedef {object} Cart
 * @property {object} [actionUrls] - Useful URLs
 * @property {object} actionUrls.removeProductLineItemUrl - Remove line endpoint
 * @property {object} actionUrls.updateQuantityUrl - Update quantity endpoint
 * @property {object[]} [approachingDiscounts]
 * @property {string} approachingDiscounts.discountMsg
 * @property {boolean} byl_isGift
 * @property {string|null} byl_giftMessage
 * @property {number|null} cartMaxSKUAmountExceeded
 * @property {number|null} cartMaxPriceExceeded
 * @property {boolean} [hasBonusProduct] True if cart has bonus products
 * @property {ProductLineItem[]} items - Items added to cart
 * @property {number} numItems - Number of items in the cart
 * @property {number} [numOfShipments] Number of shipments
 * @property {object} resources
 * @property {string} resources.emptyCartMsg
 * @property {string} resources.giftMessage
 * @property {string} resources.isGift
 * @property {string} resources.maxPriceExceededMsg
 * @property {string} resources.maxSKUAmountExceededMsg
 * @property {string} resources.minicartCountOfItems
 * @property {string} resources.numberOfItems
 * @property {Shipping[]} [shipments] Cart shipments data
 * @property {Totals} [totals] - Cart price totals
 * @property {string} uuid Cart Unique identifier
 * @property {boolean} [valid]
 */

/**
 * SFRA Order model
 * JSDoc with some common properties.
 * More properties: /models/order.js
 * @typedef {object} Order
 * @property {Billing} billing - Order billing data
 * @property {ProductLineItems} items - Items added to order
 * @property {Shipping[]} shipping - Order shipping data
 * @property {Totals} totals - Order price totals
 */

/**
 * SFRA Category model
 * JSDoc with some common properties.
 * More properties: /models/categories.js
 *
 * @typedef {object} Category
 * @property {string} id - The ID of the category
 * @property {string} name - The name of the category
 * @property {string} url - Relative pretty URL
 * @property {string} urlAbs - Absolute pretty url
 */

/**
 * SFRA Full product model
 * JSDoc with some common properties.
 * More properties: /models/product/fullProduct.js
 * @typedef {object} FullProduct
 * @property {boolean} available - True if in stock
 * @property {string} id - The ID of the product
 * @property {string} longDescription - The description of the product
 * @property {string} productName - The name of the product
 * @property {string} productType - The type (variationGroup, variant)
 * @property {string} shortDescription - The description of the product
 * @property {string} uuid - The UUID of the product
 * @property {FullProduct[]} bylCompleteTheLook - Products for slider (in server is ID[])
 * @property {FullProduct[]} bylEinsteinRecommendations - Products for slider (in server is ID[])
 * @property {FullProduct[]} bylExplicitRecommendations - Products for slider (in server is ID[])
 * @property {VariationAttribute[]} variationAttributes - Variation attributes
 */

/** OMS availabilitites node response
 * @typedef {Object} OMSAvailabilitiesNode
 * @property {string} errorMessage
 * @property {Object[]} [data]
 * @property {string} data.itemId
 * @property {Object[]} data.shipNodeAvailabilities
 * @property {number} data.shipNodeAvailabilities.availableCapacity
 * @property {number} data.shipNodeAvailabilities.availableQuantity
 * @property {string} data.shipNodeAvailabilities.shipNode
 */

/**
 * Data related to an image.
 * @typedef {object} ProductImage
 * @property {string} alt - Alternative text of the image
 * @property {string} url - The image url (can be relative or absolute)
 * @property {string} absURL - The image absolute url
 * @property {string} title - Title of the image
 */

/**
 * Equivalent to "productImages" SFRA Model.
 * For a list of targeted image types (ex: 'small', 'medium', 'large'...), the
 * model is an object which contains one array of images for each type.
 * @typedef {Object.<string, ProductImage[]>} ProductImages
 */

/**
 * It refers to an scalable image and its diferent variants or sizes.
 * Each key is an image type (or size) and its value the related ProductImage.
 * @typedef {Object.<string, ProductImage>} ProductImageSet
 */

/**
 * It refers to the model defined by SF for the items stored in the SFRA cart.
 * JSDoc with some common properties.
 * More properties: /models/productLineItems.js
 * @typedef {Object} ProductLineItems
 * @property {ProductLineItem[]} items
 * @property {number} totalQuantity
 */

/**
 * It refers to the model defined by SF for the items stored in the SFRA cart.
 * JSDoc with some common properties.
 * More properties: /models/productLineItem/productLineItem.js
 * @typedef {Object} ProductLineItem
 * @property {Boolean} available - true if the line item is available
 * @property {Boolean} bylOnlineExclusive - true if the line item is onlineExclusive
 * @property {Object} [customizationColor] - Color data of the customization
 * @property {string} [customizationColor.code] - Color code of the customization
 * @property {string} [customizationColor.name] - Color name of the customization
 * @property {string} [customizationColor.text] - Color translated text of the customization
 * @property {string} [customizationText] - Text of the customization
 * @property {string} [doubleLookPID] - the ID of the double look added to cart
 * @property {string} id - the ID of the variant added to cart
 * @property {boolean} [isCustomization] - True if the product is a customization
 * @property {string} parentCategoryName - line item parent category name
 * @property {Object} price - line item price object
 * @property {Number|null} price.discount - line item price discount
 * @property {Object|null} price.list - line item price list
 * @property {string} price.list.currency - line item price list currency
 * @property {string} price.list.decimalPrice - line item price list decimal price
 * @property {string} price.list.formatted - line item price list formatted value
 * @property {Number} price.list.value - line item price list value
 * @property {Object} price.sales - line item price sales
 * @property {string} price.sales.currency - line item price sales currency
 * @property {string} price.sales.decimalPrice - line item price sales decimal price
 * @property {string} price.sales.formatted - line item price sales formatted value
 * @property {Number} price.sales.value - line item price sales value
 * @property {Object} priceTotal - total price
 * @property {Number} priceTotal.floatPrice - numeric price
 * @property {string} priceTotal.price - price with currency
 * @property {string} priceTotal.renderedPrice - HTML of the price to show
 * @property {string} productName - the name of the product
 * @property {Number} quantity - number of units added
 * @property {Object} unitPrice - price by unit
 * @property {Number} unitPrice.floatPrice - numeric price
 * @property {string} unitPrice.price - price with currency
 * @property {string} unitPrice.renderedPrice - HTML of the price to show
 * @property {string} uuid - unique ID of the product
 * @property {string} UUID - unique ID of the line item
 * @property {Object[]} variationAttributes - Product variation attributes
 * @property {string} variationAttributes.attributeId - Product variation attribute id
 * @property {string} variationAttributes.displayName - Product variation attribute display name
 * @property {string} variationAttributes.displayValue - Product variation attribute display value
 * @property {string} variationAttributes.id - Product variation attribute id
 * @property {string} variationAttributes.value - Product variation attribute value
 */

/**
 * Refinement options for the product search
 * @typedef {object} ProductSearchRefinementValue
 * @property {string} id
 * @property {string} type
 * @property {string} displayValue
 * @property {boolean} selected
 * @property {boolean} selectable
 * @property {string} title
 * @property {string} url
 * @property {string} value
 */

/**
 * Refinement options for the product search
 * @typedef {object} ProductSearchRefinement
 * @property {string} displayName
 * @property {boolean} isCategoryRefinement
 * @property {boolean} isAttributeRefinement
 * @property {boolean} isPriceRefinement
 * @property {ProductSearchRefinementValue[]} values
 */

/**
 * Search related data. It has more properties than defined in this type definition.
 * @typedef {object} ProductSearch
 * @property {object} category - Current category data (id, name, pageDescription, pageKeywords, pageTitle)
 * @property {number} count - total results
 * @property {boolean} isCategorySearch
 * @property {boolean} isRefinedCategorySearch
 * @property {boolean} isSearchSuggestionsAvailable
 * @property {number} pageNumber - search results page number
 * @property {number} pageSize - results by page
 * @property {string} permalink - URL to this page search results
 * @property {object[]} products - products array returned by the search
 * @property {string|null} searchKeywords - Search phrase of a non-category-search (q=)
 * @property {SortingOptions} productSort - sorting info
 * @property {ProductSearchRefinement[]} refinements - available refinements
 * @property {ProductSearchRefinementValue[]} selectedFilters - applied refinement values
 */

/**
 * Single sorting option for product search
 * @typedef {object} SortingOption
 * @property {string} displayName - Name of the sorting option
 * @property {string} id - ID of the option
 * @property {string} url - URL to add/remove this sorting rule
 * @property {string} sortingRuleID - ID of the related sortuingRule (srule param value)
 */

/**
 * Sorting options and selected one for product search
 * @typedef {object} SortingOptions
 * @property {SortingOption} options - Name of the sorting option
 * @property {string} ruleId - ID of the selected sorting rule
 */

/**
 * @typedef {Object} SkuStock
 * @property {string[]} available - A list of available sizes/skus.
 * @property {string[]} notAvailable - A list of not available sizes/skus.
 * @property {string} type - The product type (Defined in frontConfig productType)
 */

/**
 * Stores object model
 * @typedef {object} StoreModel
 * @property {boolean} acceptsInStorePickup
 * @property {string} address1
 * @property {string} address2
 * @property {string} city
 * @property {string} cityLabel
 * @property {string} countryCode
 * @property {number} courierCode
 * @property {string} ID
 * @property {boolean} isExpress - True if store accepts any express shipping method
 * @property {boolean} isDropPoint
 * @property {boolean} isMarketplace
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} name
 * @property {string} phone
 * @property {string} postalCode
 * @property {number} returnsCourierCode
 * @property {SkuStock} skuStock
 * @property {string} stateCode
 * @property {string} storeHours
 */

/**
 * @typedef {Object.<string, SkuStock>} StoresSkuStock Object keys will be storeIds
 */

/**
 * Stores object result
 * @typedef {object} StoresModel
 * @property {string} actionUrl
 * @property {string} googleMapsApi - Google maps url with API key
 * @property {string} locations - stringified locations as an array of objects with name, latitude, longitude, infoWindowHtml, id
 * @property {number} radius - applied radius form search
 * @property {number[]} raduisOptions array of search radius [10, 100, 250, 500, 1000, 2000, 50000, 10000]
 * @property {object} searchKey - object cointainig latitude and longitude { lat, long }
 * @property {number} searchKey.lat
 * @property {number} searchKey.long
 * @property {StoreModel[]} stores
 * @property {string} storesResultsHtml - stringified html result for stores
 */

/**
 * Product Variation attribute
 * @typedef {object} VariationAttribute
 * @property {string} attributeId - ID of the attribute
 * @property {string} displayName - Name of the attribute
 * @property {string} id - ID of the attribute
 * @property {string} resetUrl - URL to deselect completely this attribute
 * @property {boolean} swatchable - is swatchable
 * @property {string} [value] - value of the attribute value in case of product
 * variants
 * @property {VariationAttributeValue[]} [values] - Related values in case of
 * variation groups
 */

/**
 * Product Variation attribute single value
 * @typedef {object} VariationAttributeValue
 * @property {string} id - value.ID,
 * @property {string} description - description of the attribute value
 * @property {string} displayValue - formatDisplayValue(attr, value)
 * @property {string} value - value of the attribute value
 * @property {boolean} selected - is selected
 * @property {boolean} selectable - can be selected
 * @property {string} url - URL to select or deselect the attribute value
 * @property {object} [images] - swatch images if it is swatchable
 * @property {object[]} [images.swatch] - swatch images array
 */

/**
 * API Stock Location
 * @typedef {object} APIStockLocation
 * @property {string} product - product sku,
 * @property {string} productDescription - string description of the product
 * @property {string} variant - variant sku
 * @property {string} ean - ean code
 * @property {number} totalStock - total stock for current store and product
 * @property {number} showcaseStock - stock in showcase
 * @property {number} reservationStock - stock in reservation
 * @property {number} onTheWayStock - stock on the way
 * @property {number} storeStock - stock in store
 */

/**
 * Countries information
 * @typedef {Object.<string, Country>} Countries Object keys will be country 2 character ISO codes
 */

/**
 * Country information
 * @typedef {Object} Country
 * @property {string} id Country 2 character ISO code
 * @property {Object.<string, Language>} languages Country languages info with locale as key
 * @property {string} market Country's market/site
 * @property {string} name Country name
 * @property {string} group Country group defined
 * @property {string} urlSetCookie Url used to set the country-lang cookie
 */

/**
 * Language information
 * @typedef {Object} Language
 * @property {string} displayLanguage Language name
 * @property {string} id Language id
 * @property {string} language Language 2 character ISO code
 * @property {string} localeHost Locale URL
 * @property {string} ocapi Locale ID for Ocapi
 * @property {string} url Locale ID for the Url
 * @property {string} urlSetCookie URL used to set the country-lang cookie
 */

/**
 * Country Groups
 * @typedef {Object.<string, CountryGroup>} CountryGroups
 */

/**
 * Country Group
 * @typedef {Object} CountryGroup
 * @property {string} id Country Group identifier
 * @property {string} text Country Group text to be shown
 * @property {Countries} countries Country information belonging to the group
 */

module.exports = {
    SFCCCardTypes: SFCCCardTypes
};
